// src/Admin/MessagesManagement.jsx — inbox with search, status filter, reply and CSV export

import React, { useMemo, useRef, useState } from 'react';
import { db } from '../Firebase/firebaseConfig';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { Modal } from 'react-bootstrap';
import ConfirmDialog from './ConfirmDialog';
import { STATUSES, messageStatus, toDate, formatDateTime, downloadCsv } from './utils';

const MessagesManagement = ({ messages }) => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [opened, setOpened] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const timer = useRef();

  const flash = (type, text) => {
    setNotice({ type, text });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setNotice(null), 4000);
  };

  const counts = useMemo(() => {
    const c = { all: messages.length, new: 0, contacted: 0, done: 0 };
    messages.forEach((m) => { c[messageStatus(m)] += 1; });
    return c;
  }, [messages]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = messages.filter(
      (m) =>
        (statusFilter === 'all' || messageStatus(m) === statusFilter) &&
        (!q || [m.name, m.email, m.subject, m.message].some((v) => String(v || '').toLowerCase().includes(q)))
    );
    const time = (m) => toDate(m.timestamp)?.getTime() || 0;
    return [...list].sort((a, b) => (sort === 'oldest' ? time(a) - time(b) : time(b) - time(a)));
  }, [messages, query, statusFilter, sort]);

  // Keep the older `read` flag in sync so anything else that reads it keeps working
  const setStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, 'contactMessages', id), { status, read: status !== 'new' });
    } catch (err) {
      console.error(err);
      flash('error', 'Could not update the status.');
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await deleteDoc(doc(db, 'contactMessages', toDelete.id));
      flash('success', 'Message deleted.');
      if (opened?.id === toDelete.id) setOpened(null);
    } catch (err) {
      console.error(err);
      flash('error', 'Could not delete the message.');
    } finally {
      setBusy(false);
      setToDelete(null);
    }
  };

  const exportCsv = () =>
    downloadCsv(
      `messages-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((m) => ({
        Name: m.name,
        Email: m.email,
        Subject: m.subject,
        Message: m.message,
        Status: messageStatus(m),
        Date: toDate(m.timestamp)?.toISOString() || '',
      }))
    );

  const replyLink = (m) => `mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject || ''))}`;
  const current = opened ? messages.find((m) => m.id === opened.id) || opened : null;

  return (
    <div className="adm-screen">
      {notice && <div className={`adm-alert adm-alert--${notice.type}`} role="status">{notice.text}</div>}

      <div className="adm-summary">
        {[['all', 'All'], ['new', 'New'], ['contacted', 'Contacted'], ['done', 'Done']].map(([key, label]) => (
          <button key={key} className={`adm-summary__item ${statusFilter === key ? 'is-active' : ''}`} onClick={() => setStatusFilter(key)}>
            <strong>{counts[key]}</strong> {label}
          </button>
        ))}
      </div>

      <div className="adm-toolbar">
        <div className="adm-search">
          <i className="fas fa-search"></i>
          <input type="search" placeholder="Search name, email, subject or message…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        <button className="adm-btn adm-btn--primary" onClick={exportCsv} disabled={!rows.length}>
          <i className="fas fa-file-arrow-down"></i> Export CSV
        </button>
      </div>

      <p className="adm-count">
        Showing <strong>{rows.length}</strong> of {counts.all}
        {(query || statusFilter !== 'all') && <button className="adm-link" onClick={() => { setQuery(''); setStatusFilter('all'); }}>Clear filters</button>}
      </p>

      {rows.length ? (
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--stack">
            <thead>
              <tr><th>From</th><th>Message</th><th>Date</th><th>Status</th><th className="adm-th-actions">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const status = messageStatus(m);
                return (
                  <tr key={m.id} className={status === 'new' ? 'is-new' : ''}>
                    <td data-label="From">
                      <div className="adm-cell">
                        <strong>{m.name || 'Unknown'}</strong>
                        <small>{m.email}</small>
                      </div>
                    </td>
                    <td data-label="Message" className="adm-msg-cell">
                      <button className="adm-msg-open" onClick={() => setOpened(m)}>
                        <strong>{m.subject || '(no subject)'}</strong>
                        <span>{m.message}</span>
                      </button>
                    </td>
                    <td data-label="Date">{formatDateTime(m.timestamp)}</td>
                    <td data-label="Status">
                      <select className={`adm-status-select adm-status--${status}`} value={status} onChange={(e) => setStatus(m.id, e.target.value)} aria-label={`Status for message from ${m.name}`}>
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Actions" className="adm-actions-cell">
                      <button className="adm-icon-btn" onClick={() => setOpened(m)} title="Read"><i className="far fa-eye"></i></button>
                      {m.email && <a className="adm-icon-btn" href={replyLink(m)} title="Reply by email"><i className="fas fa-reply"></i></a>}
                      <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => setToDelete(m)} title="Delete" aria-label={`Delete message from ${m.name}`}><i className="far fa-trash-can"></i></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="adm-empty adm-empty--box">{counts.all ? 'No messages match these filters.' : 'No messages yet.'}</p>
      )}

      {/* Read a message */}
      <Modal show={!!current} onHide={() => setOpened(null)} centered size="lg" className="adm-modal">
        {current && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{current.subject || '(no subject)'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="adm-msg-meta">
                <span><i className="far fa-user"></i> {current.name}</span>
                <a href={`mailto:${current.email}`}><i className="far fa-envelope"></i> {current.email}</a>
                <span><i className="far fa-clock"></i> {formatDateTime(current.timestamp)}</span>
              </div>
              <p className="adm-msg-body">{current.message}</p>
            </Modal.Body>
            <Modal.Footer>
              <select className={`adm-status-select adm-status--${messageStatus(current)}`} value={messageStatus(current)} onChange={(e) => setStatus(current.id, e.target.value)} aria-label="Status">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <a className="adm-btn adm-btn--primary" href={replyLink(current)}><i className="fas fa-reply"></i> Reply by email</a>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <ConfirmDialog show={!!toDelete} title="Delete message" busy={busy} onConfirm={confirmDelete} onCancel={() => setToDelete(null)}>
        Delete the message from <strong>{toDelete?.name}</strong>? This cannot be undone.
      </ConfirmDialog>
    </div>
  );
};

export default MessagesManagement;
